import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import api from '../../services/api.js';
import { useTagSettings } from '../../context/TagSettingsContext';

/**
 * Hook para operações de tags em artigos
 * @param {Function} setArticles - Função para atualizar o estado dos artigos
 * @param {Function} showSnackbar - Função para exibir notificações
 * @param {string} currentTagId - ID da tag atual (se estiver na página de tag)
 * @returns {Object} Objeto com funções de manipulação de tags (addTags, removeTag)
 */
const useArticleTags = (setArticles, showSnackbar, currentTagId = null) => {
  const { refreshTagCounts } = useTagSettings();
  const queryClient = useQueryClient();

  // Notificação
  const notify = useCallback((message, severity = 'success') => {
    if (typeof showSnackbar === 'function') {
      showSnackbar(message, severity);
    }
  }, [showSnackbar]);

  // Atualizar artigo no estado local
  const updateArticleInState = useCallback((articleId, updaterFn) => {
    setArticles(prevArticles =>
      prevArticles.map(article =>
        article._id === articleId
          ? updaterFn(article)
          : article
      )
    );
  }, [setArticles]);

  // Adicionar tags a um artigo
  const addTags = useCallback(async (articleId, newTags) => {
    try {
      if (!articleId) throw new Error('ID do artigo é obrigatório');
      const tagsToAdd = Array.isArray(newTags) ? newTags : [newTags];
      const currentArticleResponse = await api.get(`/articles/${articleId}`);
      const currentArticle = currentArticleResponse.data.data;
      const existingTagIds = currentArticle.tags.map(tag => typeof tag === 'object' ? tag._id : tag);
      const newTagIds = tagsToAdd.map(tag => typeof tag === 'object' ? tag._id : tag);
      const allTagIds = [...new Set([...existingTagIds, ...newTagIds])];
      await api.patch(`/articles/${articleId}/tags`, { tags: allTagIds });
      const updatedArticleResponse = await api.get(`/articles/${articleId}`);
      const updatedArticle = updatedArticleResponse.data.data;
      updateArticleInState(articleId, () => updatedArticle);
      notify('Tags adicionadas com sucesso');
      console.log('[useArticleTags] Calling refreshTagCounts after addTags');

      refreshTagCounts();

      // Invalidar cache do React Query para evitar sobrescrita por revalidação em background
      queryClient.invalidateQueries({ queryKey: ['articles'] });

      // Notificar TagSettingsContext para atualizar contagens/capa das tags
      window.dispatchEvent(new CustomEvent('tags-updated'));

      return true;
    } catch (err) {
      console.error('Erro ao adicionar tags:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Erro ao adicionar tags';
      notify(errorMessage, 'error');
      return false;
    }
  }, [updateArticleInState, notify, refreshTagCounts]);

  // Remover tag de um artigo
  const removeTag = useCallback(async (articleId, tagId) => {
    try {
      if (!articleId) throw new Error('ID do artigo é obrigatório');
      const id = typeof tagId === 'object' ? tagId._id : tagId;

      // 1. Obter estado atual (opcional, mas bom para validação)
      const currentArticleResponse = await api.get(`/articles/${articleId}`);
      const currentArticle = currentArticleResponse.data.data;

      const existingTagIds = currentArticle.tags.map(tag => {
        const tId = typeof tag === 'object' ? tag._id : tag;
        return tId.toString();
      });

      // 2. Filtrar a tag a ser removida
      const updatedTagIds = existingTagIds.filter(existingId => existingId !== id.toString());

      // 3. Enviar atualização para API
      await api.patch(`/articles/${articleId}/tags`, { tags: updatedTagIds });

      // 4. Buscar artigo ATUALIZADO (com tags populadas) para garantir consistência visual
      const updatedArticleResponse = await api.get(`/articles/${articleId}`);
      const updatedArticle = updatedArticleResponse.data.data;

      // 5. Atualizar estado local
      // Se estamos na página de uma tag específica (currentTagId), remover artigo se não tiver mais essa tag
      let articleRemoved = false;
      if (currentTagId) {
        // Verificar se o artigo ainda tem a tag atual
        const stillHasCurrentTag = updatedArticle.tags.some(tag => {
          const tagId = typeof tag === 'object' ? tag._id : tag;
          return tagId?.toString() === currentTagId.toString();
        });
        
        if (!stillHasCurrentTag) {
          // Artigo não tem mais a tag atual, remover da lista
          setArticles(prev => prev.filter(article => article._id !== articleId));
          articleRemoved = true;
          console.log('[useArticleTags] Article removed from list - no longer has tag', currentTagId);
        } else {
          // Artigo ainda tem a tag, apenas atualizar
          updateArticleInState(articleId, () => updatedArticle);
        }
      } else {
        // Não estamos em página de tag específica, apenas atualizar
        updateArticleInState(articleId, () => updatedArticle);
      }

      // 6. Notificar e atualizar contagens
      notify('Tag removida com sucesso');
      console.log('[useArticleTags] Calling refreshTagCounts after removeTag');
      refreshTagCounts();

      // Notificar TagSettingsContext para atualizar contagens/capa das tags
      window.dispatchEvent(new CustomEvent('tags-updated'));

      // SEMPRE invalidar o cache da seção Artigos para que ao voltar pra lá
      // o artigo apareça sem a tag removida.
      // OBS: invalidateQueries(['articles']) afeta apenas o cache da tela Artigos
      // (useArticlesQuery), NÃO a lista de artigos da tela Tags (TagSettingsContext),
      // então não há risco do artigo "reaparecer" na tela de Tags.
      queryClient.invalidateQueries({ queryKey: ['articles'] });

      return true;
    } catch (err) {
      console.error('Erro ao remover tag:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Erro ao remover tag';
      notify(errorMessage, 'error');
      return false;
    }
  }, [updateArticleInState, notify, refreshTagCounts, queryClient, currentTagId]);

  return {
    addTags,
    removeTag
  };
};

export default useArticleTags; 